import { useState, type ChangeEvent, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, CardBody, ConfirmDialog, DataTable, EmptyState, Input, LoadingState, Select, type Column } from "@agentnative/ui";
import { apiErrorMessage } from "../lib/api/client";
import { listApps } from "../lib/api/catalog";
import { OneTimeSecretAlert } from "../components/OneTimeSecret";
import {
  createOAuthResource,
  deleteOAuthResource,
  listOAuthResources,
  provisionIntrospectionClient,
  resourceUrlProblem,
  scopeNameProblem,
  updateOAuthResource,
  type OAuthResource,
  type OneTimeSecret,
} from "../lib/api/oauth";

interface ScopeRow {
  name: string;
  description: string;
  isDefault: boolean;
  /** The scope already exists on the saved resource, so an old-style name stays editable. */
  existing: boolean;
}

function rowsFromResource(r: OAuthResource): ScopeRow[] {
  return Object.entries(r.scopes).map(([name, description]) => ({
    name,
    description,
    isDefault: r.default_scopes.includes(name),
    existing: true,
  }));
}

/** One problem per row index, or null when the rows are valid. */
function scopeRowProblems(rows: ScopeRow[]): { problems: (string | null)[]; ok: boolean } {
  const seen = new Set<string>();
  const problems = rows.map((r) => {
    const name = r.name.trim();
    const p = scopeNameProblem(name, r.existing);
    if (p) return p;
    if (seen.has(name)) return "Duplicate scope name.";
    seen.add(name);
    if (r.description.trim() === "") return "Enter the description shown on the consent screen.";
    if (r.description.trim().length > 200) return "Description is too long (200 characters at most).";
    return null;
  });
  return { problems, ok: rows.length > 0 && rows.length <= 50 && problems.every((p) => p === null) };
}

interface FormState {
  id: string | null; // null: creating
  uri: string;
  name: string;
  appId: string;
  rows: ScopeRow[];
  enabled: boolean;
}

const emptyRow: ScopeRow = { name: "", description: "", isDefault: false, existing: false };
const emptyForm: FormState = { id: null, uri: "", name: "", appId: "", rows: [{ ...emptyRow }], enabled: true };

export function AdminOAuthResourcesRoute() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);
  const [secret, setSecret] = useState<OneTimeSecret | null>(null);
  const [deleting, setDeleting] = useState<OAuthResource | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["oauth-resources"] });

  const resourcesQuery = useQuery({ queryKey: ["oauth-resources"], queryFn: listOAuthResources });
  const appsQuery = useQuery({ queryKey: ["catalog-apps-for-oauth"], queryFn: () => listApps({ limit: 100 }) });

  const rowCheck = form ? scopeRowProblems(form.rows) : { problems: [], ok: false };
  const uriProblem = form && form.id === null ? resourceUrlProblem(form.uri) : null;
  const formValid = !!form && form.name.trim() !== "" && (form.id !== null || (!uriProblem && form.appId !== "")) && rowCheck.ok;

  const saveMutation = useMutation({
    mutationFn: async (f: FormState) => {
      const scopes: Record<string, string> = {};
      for (const r of f.rows) scopes[r.name.trim()] = r.description.trim();
      const default_scopes = f.rows.filter((r) => r.isDefault).map((r) => r.name.trim());
      const base = { name: f.name.trim(), scopes, default_scopes, enabled: f.enabled };
      if (f.id) return updateOAuthResource(f.id, base);
      return createOAuthResource({ ...base, resource_uri: f.uri, app_id: f.appId });
    },
    onSuccess: () => {
      setForm(null);
      refresh();
    },
  });
  const toggleMutation = useMutation({
    mutationFn: (r: OAuthResource) =>
      updateOAuthResource(r.id, { name: r.name, scopes: r.scopes, default_scopes: r.default_scopes, enabled: !r.enabled }),
    onSuccess: refresh,
  });
  const deleteMutation = useMutation({
    mutationFn: (r: OAuthResource) => deleteOAuthResource(r.id),
    onSuccess: () => {
      setDeleting(null);
      refresh();
    },
  });
  const introspectionMutation = useMutation({
    mutationFn: (r: OAuthResource) => provisionIntrospectionClient(r.id),
    onSuccess: (s) => {
      setSecret(s);
      refresh();
    },
  });

  function setRow(i: number, patch: Partial<ScopeRow>) {
    if (!form) return;
    setForm({ ...form, rows: form.rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  }

  function removeRow(i: number) {
    if (!form) return;
    setForm({ ...form, rows: form.rows.filter((_, j) => j !== i) });
  }

  function edit(r: OAuthResource) {
    setForm({
      id: r.id,
      uri: r.resource_uri,
      name: r.name,
      appId: r.app_id,
      rows: rowsFromResource(r),
      enabled: r.enabled,
    });
  }

  const resources = resourcesQuery.data?.resources ?? [];
  const columns: Column<OAuthResource>[] = [
    {
      key: "name",
      label: "Resource",
      render: (r) => (
        <div>
          <div className="font-medium">{r.name}</div>
          <div className="break-all font-mono text-xs text-[var(--color-neutral-500)]">{r.resource_uri}</div>
        </div>
      ),
    },
    { key: "app", label: "Licensed app", render: (r) => r.app_name || r.app_id },
    { key: "scopes", label: "Scopes", render: (r) => <span className="font-mono text-xs">{Object.keys(r.scopes).join(", ")}</span> },
    {
      key: "introspection",
      label: "Introspection client",
      render: (r) => (r.introspection_client_id ? <span className="font-mono text-xs">{r.introspection_client_id}</span> : "none"),
    },
    { key: "state", label: "Status", render: (r) => (r.enabled ? <Badge variant="success">Enabled</Badge> : <Badge variant="warning">Disabled</Badge>) },
    {
      key: "actions",
      label: "",
      render: (r) => (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => edit(r)} aria-label={`Edit ${r.name}`}>
            Edit
          </Button>
          <Button size="sm" variant="secondary" disabled={toggleMutation.isPending} onClick={() => toggleMutation.mutate(r)} aria-label={`${r.enabled ? "Disable" : "Enable"} ${r.name}`}>
            {r.enabled ? "Disable" : "Enable"}
          </Button>
          <Button size="sm" variant="secondary" disabled={introspectionMutation.isPending} onClick={() => introspectionMutation.mutate(r)} aria-label={`${r.introspection_client_id ? "Rotate" : "Create"} introspection secret for ${r.name}`}>
            {r.introspection_client_id ? "Rotate introspection secret" : "Create introspection client"}
          </Button>
          {!r.enabled && (
            <Button size="sm" variant="danger" onClick={() => setDeleting(r)} aria-label={`Delete ${r.name}`}>
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  const mutationError = [saveMutation, toggleMutation, deleteMutation, introspectionMutation].find((m) => m.isError);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">OAuth resources</h1>
        <Button onClick={() => setForm({ ...emptyForm })} disabled={form !== null}>
          Register a resource
        </Button>
      </div>
      <p className="text-sm text-[var(--color-neutral-500)]">
        A resource is one app&apos;s MCP server that Claude, Claude Code and agents may be given tokens for. A token is bound
        to exactly one resource URL, so the URL must be written exactly as the app publishes it in its protected resource
        metadata: <code>https</code> only, lower-case host, no trailing slash, no wildcard. It cannot be changed afterwards;
        register a new resource instead.
      </p>
      <Alert variant="info" title="Who can get access">
        A token is issued only when the person (or agent&apos;s organization) belongs to an organization that holds an active
        or trialing license for the resource&apos;s app. The app then maps the token&apos;s organization to its own tenant
        through the organization link (&quot;Connect to Agent Native&quot;) and applies the person&apos;s own role in the app
        on top of the granted scopes. An organization with no license, or no link in the app, gets no access even with a
        valid token. Disabling a resource stops refreshes and introspection at once; deleting one (only when disabled and not
        used by an agent) also revokes its grants.
      </Alert>

      {secret && (
        <OneTimeSecretAlert label="Introspection client" clientId={secret.client_id} secret={secret.client_secret} onDismiss={() => setSecret(null)} />
      )}
      {mutationError && (
        <Alert variant="danger" title="Action failed">
          {apiErrorMessage(mutationError.error)}
        </Alert>
      )}

      {form && (
        <Card padding="lg">
          <CardBody>
            <h2 className="mb-3 text-lg font-semibold">{form.id ? "Edit resource" : "Register a resource"}</h2>
            <form
              className="space-y-4"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                if (formValid) saveMutation.mutate(form);
              }}
            >
              <Input
                label="MCP URL"
                value={form.uri}
                disabled={form.id !== null}
                error={form.uri !== "" ? (uriProblem ?? undefined) : undefined}
                helperText="Exactly the resource value the app publishes, for example https://projectpanda.sparbz.cloud/mcp"
                onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, uri: e.target.value })}
              />
              <Input label="Name" value={form.name} onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })} />
              <Select
                label="Licensed app"
                value={form.appId}
                disabled={form.id !== null}
                placeholder="Choose the catalog app"
                helperText="The license check for tokens uses this app."
                options={(appsQuery.data?.apps ?? []).map((a) => ({ value: a.id, label: a.name }))}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setForm({ ...form, appId: e.target.value })}
              />
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Scopes</legend>
                <p className="text-xs text-[var(--color-neutral-500)]">
                  Name each scope as <code>app:action</code> in lower case (for example <code>ledger:read</code>) and describe it in
                  plain words: the description is what the person sees on the consent screen. Tick &quot;Default&quot; for
                  scopes granted when a client asks for none.
                </p>
                {form.rows.map((row, i) => (
                  <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-[14rem_1fr_auto_auto] sm:items-start">
                    <Input
                      label={i === 0 ? "Name" : undefined}
                      aria-label={`Scope ${i + 1} name`}
                      value={row.name}
                      error={row.name !== "" || row.description !== "" ? (rowCheck.problems[i] ?? undefined) : undefined}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setRow(i, { name: e.target.value })}
                    />
                    <Input
                      label={i === 0 ? "Description" : undefined}
                      aria-label={`Scope ${i + 1} description`}
                      value={row.description}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setRow(i, { description: e.target.value })}
                    />
                    <label className="flex items-center gap-1 pt-2 text-sm sm:pt-8">
                      <input type="checkbox" checked={row.isDefault} onChange={(e) => setRow(i, { isDefault: e.target.checked })} />
                      Default
                    </label>
                    <div className="sm:pt-7">
                      <Button type="button" size="sm" variant="secondary" disabled={form.rows.length <= 1} onClick={() => removeRow(i)} aria-label={`Remove scope ${i + 1}`}>
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
                <Button type="button" size="sm" variant="secondary" disabled={form.rows.length >= 50} onClick={() => form && setForm({ ...form, rows: [...form.rows, { ...emptyRow }] })}>
                  Add a scope
                </Button>
              </fieldset>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
                Enabled
              </label>
              <div className="flex gap-2">
                <Button type="submit" disabled={!formValid || saveMutation.isPending}>
                  {form.id ? "Save changes" : "Register resource"}
                </Button>
                <Button type="button" variant="secondary" onClick={() => setForm(null)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      {resourcesQuery.isLoading ? (
        <LoadingState message="Loading resources..." />
      ) : resourcesQuery.isError ? (
        <Alert variant="danger" title="Failed to load resources">
          {apiErrorMessage(resourcesQuery.error)}
        </Alert>
      ) : resources.length === 0 ? (
        <EmptyState title="No resources" description="Register an app's MCP server to let clients request tokens for it." />
      ) : (
        <Card padding="none">
          <DataTable<OAuthResource> data={resources} columns={columns} emptyMessage="No resources." />
        </Card>
      )}

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Delete resource"
        message={`Delete ${deleting?.name ?? "this resource"}? Every grant for it is revoked. This cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={() => deleting && deleteMutation.mutate(deleting)}
      />
    </div>
  );
}
